//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { createSelector } from 'reselect';

import { IScheduledInstagramPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { InstagramSchedulePostFilter } from '@instagram-account/redux/schedule/instagramScheduleModel';
import {
  getInstagramPostType,
  getInstagramStandardPostDetails,
  getStoryGroupCustomizationText,
} from '@instagram-account/redux/schedule/instagramScheduleUtils';
import { getOrderedPosts } from '@instagram-account/redux/schedule/selectors/getOrderedPosts';
import { ICuralateStore } from '@models/AppState';
import { hasFullMediaAndNetworkItem } from '@models/IMlsMediaItem';

/**
 * Filters the ordered scheduled posts based on the current postFilter value.
 */
export const getFilteredPostsImpl = (
  scheduledPosts: IScheduledInstagramPost[],
  postFilter: InstagramSchedulePostFilter,
  captionSearchText?: string,
) => {
  return (
    scheduledPosts
      /**
       * To avoid display errors all over the calendar, we just don't show posts that don't have media
       * items attached. Any post that is filtered out here will have already been logged at load for easy tracing.
       */
      .filter(hasFullMediaAndNetworkItem)
      /* Filter posts per the current post type filter */
      .filter((post) => {
        if (postFilter === 'all') {
          return true;
        } else {
          return getInstagramPostType(post) === postFilter;
        }
      })
      /**
       * Filter feed posts based on whether the caption contains
       * the captionSearchText, and story posts based on whether any
       * customization value contains the captionSearchText.
       *
       * This filter is case-insensitive.
       */
      .filter((scheduledPost) => {
        const { caption, customizations, storyClips } =
          getInstagramStandardPostDetails(scheduledPost);
        const postType = scheduledPost.postType;

        if (captionSearchText) {
          const searchText = captionSearchText.toLowerCase();

          if (
            postType === UniversalPostType.InstagramStoryGroupAutomaticPost ||
            postType === UniversalPostType.InstagramStoryGroupPost
          ) {
            return getStoryGroupCustomizationText(storyClips).some(
              (customization) =>
                customization.toLowerCase().includes(searchText),
            );
          } else if (
            postType === UniversalPostType.InstagramReelsAutomaticPost ||
            postType === UniversalPostType.InstagramReelsManualPost
          ) {
            if (customizations) {
              const customizationKeys = Object.keys(
                customizations,
              ) as (keyof typeof customizations)[];

              return customizationKeys.some((key) => {
                const customization = customizations[key];

                if (typeof customization === 'string') {
                  return customization.toLowerCase().includes(searchText);
                }

                if (Array.isArray(customization)) {
                  return customization.some((value) => {
                    return value.toLowerCase().includes(searchText);
                  });
                }

                return false;
              });
            }

            return false;
          } else {
            return caption?.toLowerCase().includes(searchText);
          }
        } else {
          return true;
        }
      })
  );
};

export const getFilteredPosts = createSelector(
  getOrderedPosts,
  (state: ICuralateStore) => state.instagram.schedule.postFilter,
  (state: ICuralateStore) => state.instagram.schedule.captionSearchText,
  getFilteredPostsImpl,
);
